import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FlagIcon from '@mui/icons-material/Flag';
import { tokens } from '../../../../../theme';
import QuestionSlot from '../components/QuestionSlot';
import QuestionNav from '../components/QuestionNav';
import QuestionDisplay from '../components/QuestionDisplay';
import { useTestSession } from '../TestSessionContext';

const QuestionUIRenderer = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {
        test, answers,
        currentSectionIndex, setCurrentSectionIndex,
        currentQuestionIndex, setCurrentQuestionIndex,
        isDirty, save,
    } = useTestSession();

    const [dividerPosition, setDividerPosition] = useState(5);
    const rightPaneRef = useRef(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('v2TestViewMode') || 'all');

    const toggleViewMode = () => {
        const next = viewMode === 'single' ? 'all' : 'single';
        setViewMode(next);
        localStorage.setItem('v2TestViewMode', next);
    };

    const sections = test?.v2_test_sections || [];
    const currentSection = sections[currentSectionIndex];
    const questions = currentSection?.v2_test_questions || [];
    const currentQuestion = questions[currentQuestionIndex];

    // "Shared passage — Questions 4–6" label for a question's group. Counts the
    // CONTIGUOUS run around this question only — if group members are scattered
    // (legacy/hand-reordered tests), each run labels just its own span instead
    // of a min–max that would overlap other passages.
    const groupLabelFor = (q) => {
        if (!q?.group_id) return null;
        const idx = questions.findIndex(x => x.id === q.id);
        if (idx === -1) return null;
        let lo = idx, hi = idx;
        while (lo > 0 && questions[lo - 1].group_id === q.group_id) lo--;
        while (hi < questions.length - 1 && questions[hi + 1].group_id === q.group_id) hi++;
        if (lo === hi) return null;
        const nums = questions.slice(lo, hi + 1).map(x => x.question_number);
        return `Shared passage — Questions ${Math.min(...nums)}–${Math.max(...nums)}`;
    };
    const sectionTotal = questions.length;

    // Build group lookup for the current section
    const groups = currentSection?.v2_question_groups || [];
    const groupById = {};
    groups.forEach((g) => (groupById[g.id] = g));

    // Split-pane resizer
    const handleMouseDown = useCallback(() => {
        const handleMouseMove = (e) => {
            if (rightPaneRef.current) {
                const newPos = (e.clientX / window.innerWidth) * 10;
                setDividerPosition(Math.max(2, Math.min(8, newPos)));
            }
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    // Navigation
    const goTo = useCallback((sIdx, qIdx) => {
        if (isDirty) save();
        setCurrentSectionIndex(sIdx);
        setCurrentQuestionIndex(qIdx);
    }, [isDirty, save, setCurrentSectionIndex, setCurrentQuestionIndex]);

    const goPrev = () => {
        if (currentQuestionIndex > 0) goTo(currentSectionIndex, currentQuestionIndex - 1);
        else if (currentSectionIndex > 0) {
            const prevQs = sections[currentSectionIndex - 1]?.v2_test_questions || [];
            goTo(currentSectionIndex - 1, prevQs.length - 1);
        }
    };

    const goNext = () => {
        if (currentQuestionIndex < questions.length - 1) goTo(currentSectionIndex, currentQuestionIndex + 1);
        else if (currentSectionIndex < sections.length - 1) goTo(currentSectionIndex + 1, 0);
    };

    const isFirst = currentSectionIndex === 0 && currentQuestionIndex === 0;
    const isLast = currentSectionIndex === sections.length - 1 && currentQuestionIndex === questions.length - 1;

    // Keyboard nav — left/right for single mode, up/down for all mode
    useEffect(() => {
        const handler = (e) => {
            if (viewMode === 'single') {
                if (e.key === 'ArrowLeft') { e.preventDefault(); document.activeElement?.blur(); goPrev(); }
                if (e.key === 'ArrowRight') { e.preventDefault(); document.activeElement?.blur(); goNext(); }
            } else {
                if (e.key === 'ArrowUp') { e.preventDefault(); document.activeElement?.blur(); programmaticScroll(Math.max(0, currentQuestionIndex - 1)); }
                if (e.key === 'ArrowDown') { e.preventDefault(); document.activeElement?.blur(); programmaticScroll(Math.min(sectionTotal - 1, currentQuestionIndex + 1)); }
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    });

    // On switch to "all" mode, scroll to the current question
    useEffect(() => {
        if (viewMode !== 'all') return;
        // Defer to next frame so the DOM has mounted
        const frame = requestAnimationFrame(() => {
            const q = questions[currentQuestionIndex];
            if (!q) return;
            programmaticScrollUntil.current = Date.now() + 300;
            const el = document.getElementById(`question-card-${q.id}`);
            const container = scrollContainerRef.current;
            if (el && container) {
                const offset = el.offsetTop;
                container.scrollTop = offset;
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [viewMode]);

    // Block print + kill outer scroll
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'v2-question-ui-print-block';
        style.textContent = '@media print { body * { visibility: hidden !important; } }';
        document.head.appendChild(style);

        // Prevent parent containers from scrolling
        const mainContent = document.querySelector('.content-area, main, [class*="content"]');
        const prevOverflows = [];
        let el = document.getElementById('v2-question-ui-root')?.parentElement;
        while (el && el !== document.body) {
            if (el.scrollHeight > el.clientHeight) {
                prevOverflows.push({ el, prev: el.style.overflow });
                el.style.overflow = 'hidden';
            }
            el = el.parentElement;
        }

        return () => {
            style.remove();
            prevOverflows.forEach(({ el, prev }) => { el.style.overflow = prev; });
        };
    }, []);

    // Flagged questions for progress bar markers
    const flaggedQuestions = questions
        .map((q, idx) => answers.get(q.id)?.flagged ? { id: q.id, index: idx } : null)
        .filter(Boolean);

    // Sync currentQuestionIndex with scroll position in "all" mode
    const scrollContainerRef = useRef(null);
    const programmaticScrollUntil = useRef(0);
    useEffect(() => {
        if (viewMode !== 'all') return;
        const container = scrollContainerRef.current;
        if (!container) return;

        let rafId = null;
        const handleScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                if (Date.now() < programmaticScrollUntil.current) return;
                // Determine current question from scroll position
                const cards = container.querySelectorAll('[data-qidx]');
                const containerTop = container.getBoundingClientRect().top;
                let closest = 0;
                let closestDist = Infinity;
                cards.forEach((card) => {
                    const dist = Math.abs(card.getBoundingClientRect().top - containerTop);
                    const idx = parseInt(card.dataset.qidx, 10);
                    if (dist < closestDist) { closestDist = dist; closest = idx; }
                });
                setCurrentQuestionIndex(closest);
            });
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [viewMode, currentSectionIndex, questions.length]);

    // Helper: scroll programmatically — instant jump, suppress scroll listener briefly
    const programmaticScroll = (idx) => {
        setCurrentQuestionIndex(idx);
        programmaticScrollUntil.current = Date.now() + 100;
        const container = scrollContainerRef.current;
        if (container) {
            const el = document.getElementById(`question-card-${questions[idx]?.id}`);
            if (el) {
                const containerRect = container.getBoundingClientRect();
                const elRect = el.getBoundingClientRect();
                const offset = elRect.top - containerRect.top + container.scrollTop;
                container.scrollTop = offset;
            }
        }
    };

    // Bail only when an index is genuinely out of range — an EMPTY section must
    // fall through so the tabs + "No questions in this section" placeholder render.
    if (!currentQuestion && viewMode === 'single' && questions.length > 0) return null;

    return (
        <Box id="v2-question-ui-root" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Shared nav — full width */}
            <QuestionNav
                sections={sections}
                currentSectionIndex={currentSectionIndex}
                onSectionNavigate={(idx) => goTo(idx, 0)}
                answers={answers}
                viewMode={viewMode}
                onToggleViewMode={toggleViewMode}
                currentQuestionIndex={currentQuestionIndex}
                sectionTotal={sectionTotal}
                isFirst={isFirst}
                isLast={isLast}
                onPrev={goPrev}
                onNext={goNext}
                onProgressClick={(idx) => goTo(currentSectionIndex, idx)}
                flaggedQuestions={flaggedQuestions}
                onFlagClick={(idx) => goTo(currentSectionIndex, idx)}
                useQuestionUi
            />

            <Box sx={{
                flex: 1, overflow: 'hidden', position: 'relative',
                display: 'flex', flexDirection: 'column',
                '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            }}>
            {questions.length === 0 ? (
                /* Empty section — placeholder instead of a stuck blank pane */
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No questions in this section — use the section tabs above to continue.
                    </Typography>
                </Box>
            ) : viewMode === 'single' ? (
                /* Single mode — split pane */
                <Box key="single" sx={{ display: 'flex', flex: 1, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' }}>
                    {/* Left pane — Question Content */}
                    <Box
                        sx={{
                            flex: dividerPosition,
                            overflow: 'auto',
                            p: 3,
                            minWidth: 200,
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                        }}
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        <Box
                            key={`${currentSectionIndex}-${currentQuestionIndex}`}
                            sx={{
                                '@keyframes fadeSlideIn': {
                                    from: { opacity: 0, transform: 'translateX(12px)' },
                                    to: { opacity: 1, transform: 'translateX(0)' },
                                },
                                animation: 'fadeSlideIn 0.15s ease-out',
                            }}
                        >
                            <QuestionDisplay
                                question={currentQuestion}
                                group={currentQuestion?.group_id ? groupById[currentQuestion.group_id] : null}
                                groupLabel={groupLabelFor(currentQuestion)}
                            />
                        </Box>
                    </Box>

                    {/* Resizer */}
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                            width: 6,
                            cursor: 'col-resize',
                            bgcolor: colors.grey[800],
                            '&:hover': { bgcolor: colors.blueAccent[700] },
                            transition: 'background-color 0.15s',
                            flexShrink: 0,
                        }}
                    />

                    {/* Right pane — Answer input */}
                    <Box ref={rightPaneRef} sx={{ flex: 10 - dividerPosition, display: 'flex', flexDirection: 'column', minWidth: 300, overflow: 'auto', px: 3, py: 2 }}>
                        <Box key={`${currentSectionIndex}-${currentQuestionIndex}`}
                            sx={{ '@keyframes fadeSlideIn': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                                animation: 'fadeSlideIn 0.15s ease-out' }}>
                            <QuestionSlot question={currentQuestion} allQuestionsInSection={questions} verticalOptions />
                        </Box>
                    </Box>
                </Box>
            ) : (
                /* All mode — scrollable question list + vertical progress sidebar (replaces scrollbar) */
                <Box key="all" sx={{ display: 'flex', flex: 1, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' }}>
                    {/* Scrollable question list — hide native scrollbar, progress sidebar replaces it */}
                    <Box ref={scrollContainerRef} sx={{
                        flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3,
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                    }}>
                        {questions.map((q, idx) => (
                            <Box key={q.id} id={`question-card-${q.id}`} data-qidx={idx} sx={{
                                display: 'flex', flexWrap: 'wrap', mb: idx < questions.length - 1 ? 3 : 0, alignItems: 'stretch',
                                border: '1px solid', borderColor: colors.grey[700], borderRadius: 1, overflow: 'hidden',
                            }}>
                                {/* Question content. A shared passage renders only on the FIRST
                                    question of its group — consecutive group-mates show just their
                                    stem/options (no repeated passage, no divider under it). */}
                                <Box
                                    sx={{
                                        flex: '1 1 400px', minWidth: 0, p: 2.5,
                                        userSelect: 'none', WebkitUserSelect: 'none',
                                    }}
                                    onContextMenu={(e) => e.preventDefault()}
                                >
                                    <QuestionDisplay
                                        question={q}
                                        group={q.group_id && questions[idx - 1]?.group_id !== q.group_id
                                            ? groupById[q.group_id] : null}
                                        groupLabel={groupLabelFor(q)}
                                    />
                                </Box>
                                {/* Vertical divider */}
                                <Box sx={{ width: '1px', bgcolor: colors.grey[700], flexShrink: 0 }} />
                                {/* Answer input */}
                                <Box sx={{ flex: '1 1 300px', minWidth: 0, p: 2.5 }}>
                                    <QuestionSlot question={q} allQuestionsInSection={questions} verticalOptions noBorder />
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Vertical progress sidebar (right, replaces scrollbar) */}
                    <Box sx={{
                        width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', py: 1.5, gap: 0.5,
                        borderLeft: '1px solid', borderColor: colors.grey[700],
                    }}>
                        <IconButton size="small" disabled={isFirst} onClick={() => programmaticScroll(Math.max(0, currentQuestionIndex - 1))} sx={{ p: 0.5 }}>
                            <NavigateBeforeIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                        </IconButton>
                        <Box sx={{ flex: 1, width: 8, borderRadius: 4, bgcolor: colors.grey[800], position: 'relative', overflow: 'visible', my: 0.5 }}>
                            {/* Flag markers */}
                            {flaggedQuestions?.map(({ id, index }) => {
                                const top = sectionTotal > 1 ? ((index + 0.5) / sectionTotal) * 100 : 50;
                                return (
                                    <FlagIcon key={id}
                                        onClick={(e) => { e.stopPropagation(); programmaticScroll(index); }}
                                        sx={{
                                            position: 'absolute', top: `${top}%`, left: '100%',
                                            transform: 'translate(2px, -50%) rotate(90deg)',
                                            fontSize: 14, color: '#f59e0b', cursor: 'pointer', zIndex: 3,
                                        }}
                                    />
                                );
                            })}
                            {questions.map((q, idx) => {
                                const hasAnswer = answers?.get(q.id)?.answer_text;
                                const segHeight = 100 / sectionTotal;
                                return (
                                    <Box
                                        key={q.id}
                                        onClick={() => programmaticScroll(idx)}
                                        sx={{
                                            position: 'absolute', left: 0, right: 0,
                                            top: `${idx * segHeight}%`, height: `${segHeight}%`,
                                            bgcolor: hasAnswer ? colors.blueAccent[500] : 'transparent',
                                            borderBottom: idx < sectionTotal - 1 ? `0.5px solid ${colors.grey[900]}` : 'none',
                                            cursor: 'pointer',
                                            opacity: 0.75,
                                            '&:hover': { opacity: 1 },
                                            transition: 'background-color 0.15s',
                                        }}
                                    />
                                );
                            })}
                            {/* Current position marker — draggable */}
                            <Box
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    programmaticScrollUntil.current = Date.now() + 60000; // suppress during drag
                                    const track = e.currentTarget.parentElement;
                                    const trackRect = track.getBoundingClientRect();
                                    const container = scrollContainerRef.current;
                                    const doScroll = (clientY) => {
                                        const pct = Math.max(0, Math.min(1, (clientY - trackRect.top) / trackRect.height));
                                        const idx = Math.max(0, Math.min(sectionTotal - 1, Math.floor(pct * sectionTotal)));
                                        setCurrentQuestionIndex(idx);
                                        if (container) {
                                            const maxScroll = container.scrollHeight - container.clientHeight;
                                            container.scrollTop = pct * maxScroll;
                                        }
                                    };
                                    const onMove = (ev) => doScroll(ev.clientY);
                                    const onUp = () => {
                                        document.removeEventListener('mousemove', onMove);
                                        document.removeEventListener('mouseup', onUp);
                                        programmaticScrollUntil.current = Date.now() + 100;
                                    };
                                    document.addEventListener('mousemove', onMove);
                                    document.addEventListener('mouseup', onUp);
                                }}
                                sx={{
                                    position: 'absolute',
                                    left: -4, right: -4,
                                    top: `${((currentQuestionIndex + 0.5) / sectionTotal) * 100}%`,
                                    transform: 'translateY(-50%)',
                                    height: 12, bgcolor: 'text.primary', borderRadius: 1, zIndex: 2,
                                    cursor: 'grab', '&:active': { cursor: 'grabbing' },
                                    transition: 'top 0.15s ease-out',
                                }}
                            />
                        </Box>
                        <IconButton size="small" disabled={isLast} onClick={() => programmaticScroll(Math.min(sectionTotal - 1, currentQuestionIndex + 1))} sx={{ p: 0.5 }}>
                            <NavigateNextIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                        </IconButton>
                    </Box>
                </Box>
            )}
            </Box>
        </Box>
    );
};

export default QuestionUIRenderer;
